import axios from 'axios';
import { getAuthHeader } from './authApi';

/**
 * Export the database as a zip file containing CSVs for all main tables.
 * @returns Blob of the zip file
 */
export const exportDatabase = async (): Promise<Blob> => {
  const response = await axios.get('/api/general/export-db', {
    headers: {
      ...getAuthHeader(),
      'Accept': 'application/zip',
    },
    responseType: 'blob',
    withCredentials: true,
  });
  return response.data;
};

/**
 * Get bettable status for each round
 * @returns Map of round names to bettable status
 */
export const getBettableByRound = async (): Promise<{ [key: string]: boolean }> => {
  const response = await axios.get('/api/general/bettable', {
    headers: getAuthHeader(),
    withCredentials: true,
  });
  return response.data;
};

/**
 * Set bettable status for all matches in a specific round
 * @param round - The round name (e.g., 'GROUP', 'ROUND_OF_32', etc.)
 * @param bettable - Whether matches in this round should be bettable
 */
export const setBettableByRound = async (round: string, bettable: boolean): Promise<void> => {
  await axios.put(
    '/api/general/bettable',
    { round, bettable },
    {
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
};

/**
 * Get whether general bets are currently open
 */
export const getGeneralBetsBettable = async (): Promise<boolean> => {
  const response = await axios.get('/api/general/general-bets-bettable', {
    headers: getAuthHeader(),
    withCredentials: true,
  });
  return response.data.bettable;
};

/**
 * Enable or disable general bets
 */
export const setGeneralBetsBettable = async (bettable: boolean): Promise<void> => {
  await axios.put(
    '/api/general/general-bets-bettable',
    { bettable },
    {
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
};

/**
 * Get visibility state for each controlled page
 */
export const getPageVisibility = async (): Promise<{ [key: string]: boolean }> => {
  const response = await axios.get('/api/general/page-visibility', {
    headers: getAuthHeader(),
    withCredentials: true,
  });
  return response.data;
};

/**
 * Show or hide a controlled page
 */
export const setPageVisibility = async (pageKey: string, visible: boolean): Promise<void> => {
  await axios.put(
    '/api/general/page-visibility',
    { page_key: pageKey, visible },
    {
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
};

/**
 * Check whether new user registration is open (no auth required)
 */
export const getSignupEnabled = async (): Promise<boolean> => {
  const response = await axios.get('/api/auth/signup-enabled');
  return response.data.enabled;
};

/**
 * Open or close new user registration (admin only)
 */
export const getMatchUpdateInterval = async (): Promise<number> => {
  const response = await axios.get('/api/general/match-update-interval', {
    headers: getAuthHeader(),
    withCredentials: true,
  });
  return response.data.minutes;
};

export const setMatchUpdateInterval = async (minutes: number): Promise<void> => {
  await axios.put(
    '/api/general/match-update-interval',
    { minutes },
    { headers: getAuthHeader(), withCredentials: true }
  );
};

export const getThirdPlaceQualifyingCount = async (): Promise<number> => {
  const response = await axios.get('/api/general/third-place-qualifying-count', {
    headers: getAuthHeader(),
    withCredentials: true,
  });
  return response.data.count;
};

export const setThirdPlaceQualifyingCount = async (count: number): Promise<void> => {
  await axios.put(
    '/api/general/third-place-qualifying-count',
    { count },
    { headers: getAuthHeader(), withCredentials: true }
  );
};

export const setSignupEnabled = async (enabled: boolean): Promise<void> => {
  await axios.put(
    '/api/general/signup-enabled',
    { enabled },
    {
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
};

/**
 * Trigger the general bets reminder notification immediately
 */
export const triggerGeneralBetsReminder = async (): Promise<void> => {
  await axios.post(
    '/api/general/general-bets-reminder',
    {},
    {
      headers: getAuthHeader(),
      withCredentials: true,
    }
  );
};

export const downloadIncompleteGeneralBetsCSV = async (): Promise<void> => {
  const response = await axios.get('/api/general/incomplete-general-bets-csv', {
    headers: getAuthHeader(),
    withCredentials: true,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'incomplete_general_bets.csv';
  a.click();
  URL.revokeObjectURL(url);
};